import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDown, ArrowRight, MessageCircle, ScanLine, Sparkles } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { ASSETS, postEvent, waLink } from "@/lib/site";

const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
const between = (value, start, end) => clamp((value - start) / (end - start));

const CHAPTERS = [
  {
    index: "01",
    status: { tr: "Tam ark taraması", en: "Full-arch scan" },
    eyebrow: { tr: "Bütün resmi görün", en: "See the whole picture" },
    title: { tr: "Tek diş değil. Bütün ağız bir sistem.", en: "Not one tooth. One complete system." },
    body: {
      tr: "Üst ve alt diş arkları, diş eti sınırları ve kapanış ilişkisi aynı dijital kayıtta birlikte değerlendirilir.",
      en: "Upper and lower arches, gingival margins and the bite relationship are considered together in one digital record.",
    },
  },
  {
    index: "02",
    status: { tr: "Oklüzyon haritası", en: "Occlusion map" },
    eyebrow: { tr: "Temasları okuyun", en: "Read every contact" },
    title: { tr: "Kapanış, temas ve simetri birlikte planlanır.", en: "Bite, contact and symmetry are planned together." },
    body: {
      tr: "Dijital analiz; formu, karşıt diş temaslarını ve gülüş hattını tek bir planlama yüzeyinde görünür kılar.",
      en: "Digital analysis brings form, opposing contacts and the smile line into one planning surface.",
    },
  },
  {
    index: "03",
    status: { tr: "Seçici restorasyon", en: "Selective restoration" },
    eyebrow: { tr: "Gerektiği kadar", en: "Only what is needed" },
    title: { tr: "Her dişe aynı işlem uygulanmaz.", en: "Not every tooth receives the same treatment." },
    body: {
      tr: "Restorasyon alanları yalnızca klinik gereklilik ve kişisel hedeflerle uyumlu olacak şekilde tek tek belirlenir.",
      en: "Restoration areas are selected individually, according to clinical need and personal goals.",
    },
  },
  {
    index: "04",
    status: { tr: "Doğal kapanış kontrolü", en: "Natural bite check" },
    eyebrow: { tr: "Formdan fonksiyona", en: "From form to function" },
    title: { tr: "Sonuç sadece beyaz değil; dengeli görünmeli.", en: "The result should look balanced, not simply white." },
    body: {
      tr: "Renk, oran ve kapanış kontrolü; doğal görünen kişisel bir gülüş hedefiyle hekim tarafından sonlandırılır.",
      en: "Shade, proportion and bite are finalised by the dentist with the goal of a natural, personal smile.",
    },
  },
];

export function FullMouthScroll() {
  const { t } = useApp();
  const sectionRef = useRef(null);
  const progressRef = useRef(-1);
  const [progress, setProgress] = useState(0);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onMedia = () => setReducedMotion(media.matches);
    onMedia();
    media.addEventListener?.("change", onMedia);
    return () => media.removeEventListener?.("change", onMedia);
  }, []);

  useEffect(() => {
    if (reducedMotion) {
      setProgress(0);
      return undefined;
    }

    let frame = 0;
    const update = () => {
      frame = 0;
      const section = sectionRef.current;
      if (!section) return;
      const rect = section.getBoundingClientRect();
      const distance = Math.max(1, rect.height - window.innerHeight);
      const next = clamp(-rect.top / distance);
      if (Math.abs(next - progressRef.current) > 0.002) {
        progressRef.current = next;
        setProgress(next);
      }
    };
    const requestUpdate = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", requestUpdate, { passive: true });
    window.addEventListener("resize", requestUpdate);
    return () => {
      window.removeEventListener("scroll", requestUpdate);
      window.removeEventListener("resize", requestUpdate);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [reducedMotion]);

  const introOut = between(progress, 0.025, 0.16);
  const narrativeProgress = clamp((progress - 0.12) / 0.88);
  const active = Math.min(CHAPTERS.length - 1, Math.floor(narrativeProgress * CHAPTERS.length));
  const visual = useMemo(() => {
    const scanOut = between(narrativeProgress, 0.22, 0.39);
    const planIn = between(narrativeProgress, 0.2, 0.38);
    const planOut = between(narrativeProgress, 0.58, 0.76);
    const finalIn = between(narrativeProgress, 0.6, 0.79);
    return {
      scan: 1 - scanOut,
      plan: planIn * (1 - planOut),
      final: finalIn,
    };
  }, [narrativeProgress]);

  return (
    <section
      ref={sectionRef}
      className="full-mouth-scroll"
      data-testid="full-mouth-scroll"
      aria-label={t({ tr: "Tam ağız dijital planlama deneyimi", en: "Full-mouth digital planning experience" })}
    >
      <div className="full-mouth-sticky">
        <div className="full-mouth-grid" aria-hidden="true" />
        <div className="full-mouth-glow full-mouth-glow-blue" aria-hidden="true" />
        <div className="full-mouth-glow full-mouth-glow-green" aria-hidden="true" />

        <div
          className="full-mouth-intro"
          style={{ opacity: 1 - introOut, visibility: introOut > 0.995 ? "hidden" : "visible", pointerEvents: introOut < 0.9 ? "auto" : "none" }}
          aria-hidden={introOut > 0.8}
        >
          <div className="full-mouth-intro-scrim" aria-hidden="true" />
          <div className="full-mouth-intro-trails" aria-hidden="true">
            <svg viewBox="0 0 800 400" fill="none">
              <defs>
                <filter id="full-mouth-inf-glow" x="-50%" y="-50%" width="200%" height="200%">
                  <feGaussianBlur stdDeviation="6" result="blur" />
                  <feMerge>
                    <feMergeNode in="blur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>
              <path d="M400,200 C310,80 80,80 80,200 C80,320 310,320 400,200 C490,80 720,80 720,200 C720,320 490,320 400,200" stroke="rgba(255,255,255,0.06)" strokeWidth="1.5" />
              <path className="inf-path" d="M400,200 C310,80 80,80 80,200 C80,320 310,320 400,200 C490,80 720,80 720,200 C720,320 490,320 400,200" stroke="#2ECBD9" strokeWidth="2.5" strokeLinecap="round" filter="url(#full-mouth-inf-glow)" />
              <path className="inf-path rev" d="M400,200 C310,80 80,80 80,200 C80,320 310,320 400,200 C490,80 720,80 720,200 C720,320 490,320 400,200" stroke="#A8D93B" strokeWidth="2.5" strokeLinecap="round" filter="url(#full-mouth-inf-glow)" />
            </svg>
          </div>

          <img src={ASSETS.zetaChar} alt="" className="full-mouth-intro-guide full-mouth-intro-zeta char-glow-zeta" />
          <img src={ASSETS.atezChar} alt="" className="full-mouth-intro-guide full-mouth-intro-atez char-glow-atez" />

          <div className="full-mouth-intro-copy">
            <span className="section-label">ZETA MIRROR</span>
            <h1>{t({ tr: "Gülüşünün diğer yüzü.", en: "The other face of your smile." })}</h1>
            <p>{t({ tr: "Tek bir dişi değil, bütün gülüş sistemini birlikte planlıyoruz.", en: "We plan the complete smile system, not a single tooth in isolation." })}</p>
            <div className="full-mouth-intro-actions">
              <Link to="/mirror" className="cta-primary" onClick={() => postEvent("mirror_started", { from: "scroll_hero" })}>
                <Sparkles size={16} /> {t({ tr: "ZETA Mirror'ı Başlat", en: "Start ZETA Mirror" })}
              </Link>
              <a href={waLink()} className="cta-ghost" target="_blank" rel="noopener noreferrer" onClick={() => postEvent("whatsapp_clicked", { from: "scroll_hero" })}>
                <MessageCircle size={16} /> {t({ tr: "WhatsApp'tan Yaz", en: "Message on WhatsApp" })}
              </a>
            </div>
          </div>

          <div className="full-mouth-intro-scroll" aria-hidden="true">
            <ArrowDown size={15} />
            {t({ tr: "Tam ağız deneyimini keşfet", en: "Explore the full-mouth experience" })}
          </div>
        </div>

        <div
          className="full-mouth-narrative"
          style={{ opacity: introOut, transform: `translate3d(0, ${(1 - introOut) * 18}px, 0)`, pointerEvents: introOut > 0.9 ? "auto" : "none" }}
          aria-hidden={introOut < 0.2}
        >
          <div className="full-mouth-topline">
            <div className="full-mouth-brandline">
              <span className="full-mouth-live-dot" />
              <span>ZETA FULL-ARCH</span>
              <span className="full-mouth-slash">/</span>
              <span className="full-mouth-muted">DIGITAL TWIN</span>
            </div>
            <div className="full-mouth-progress-label">
              <span>{String(active + 1).padStart(2, "0")}</span>
              <span className="full-mouth-muted">/ 04</span>
            </div>
          </div>

          <div className="full-mouth-layout">
            <div className="full-mouth-copy">
              {CHAPTERS.map((chapter, index) => (
                <article
                  key={chapter.index}
                  className={`full-mouth-chapter ${active === index ? "is-active" : ""}`}
                  aria-hidden={active !== index}
                >
                  <div className="full-mouth-kicker">
                    <span>{chapter.index}</span>
                    <span>{t(chapter.eyebrow)}</span>
                  </div>
                  <h2>{t(chapter.title)}</h2>
                  <p>{t(chapter.body)}</p>
                  {index === CHAPTERS.length - 1 && (
                    <Link
                      to="/mirror"
                      className="full-mouth-cta"
                      tabIndex={active === index ? 0 : -1}
                      onClick={() => postEvent("mirror_started", { from: "full_mouth_scroll" })}
                    >
                      {t({ tr: "7 adımlı ZETA Mirror'ı başlat", en: "Start the 7-step ZETA Mirror" })}
                      <ArrowRight size={16} />
                    </Link>
                  )}
                </article>
              ))}
            </div>

            <div className="full-mouth-visual-wrap">
              <div className="full-mouth-orbit full-mouth-orbit-one" />
              <div className="full-mouth-orbit full-mouth-orbit-two" />
              <div className="full-mouth-crosshair" />
              <div className="full-mouth-image-stage">
                <img
                  src="/assets/site/full-mouth-scan.webp"
                  alt=""
                  className="full-mouth-image"
                  style={{ opacity: visual.scan, transform: `translate3d(0, ${narrativeProgress * -2.5}%, 0) scale(${1 + narrativeProgress * 0.025})` }}
                />
                <img
                  src="/assets/site/full-mouth-plan.webp"
                  alt=""
                  className="full-mouth-image"
                  style={{ opacity: visual.plan, transform: `translate3d(0, ${(0.5 - narrativeProgress) * 4}%, 0) scale(${0.98 + narrativeProgress * 0.04})` }}
                />
                <img
                  src="/assets/site/full-mouth-final.webp"
                  alt=""
                  className="full-mouth-image"
                  style={{ opacity: visual.final, transform: `translate3d(0, ${(1 - narrativeProgress) * 3}%, 0) scale(${0.98 + narrativeProgress * 0.025})` }}
                />
                <div className="full-mouth-scanline" style={{ top: `${8 + narrativeProgress * 84}%` }} />
                <div className="full-mouth-vignette" />
              </div>

              <div className="full-mouth-readout full-mouth-readout-left">
                <span>{t(CHAPTERS[active].status)}</span>
                <strong>{Math.round(narrativeProgress * 100)}%</strong>
              </div>
              <div className="full-mouth-readout full-mouth-readout-right">
                <span>MAXILLA</span>
                <span>MANDIBLE</span>
                <span>OCCLUSION</span>
              </div>
            </div>
          </div>

          <div className="full-mouth-bottomline">
            <div className="full-mouth-scroll-hint">
              <ArrowDown size={14} />
              {t({ tr: "Süreci incelemek için kaydır", en: "Scroll to inspect the process" })}
            </div>
            <div className="full-mouth-disclaimer">
              <ScanLine size={14} />
              {t({ tr: "Görsel anlatımdır; tanı ve sonuç garantisi değildir.", en: "Visual explanation; not a diagnosis or outcome guarantee." })}
            </div>
          </div>

          <div className="full-mouth-rail" aria-hidden="true">
            <span style={{ transform: `scaleY(${Math.max(narrativeProgress, 0.01)})` }} />
          </div>
        </div>
      </div>
    </section>
  );
}
